/**
 * Row view models for the Accounts and Machines cards of the Roost exceptions
 * dashboard (WI 34293, WI 34532). The Resetting-soon view model lives beside
 * it in reset-soon.logic.ts, split out to keep both files under the 500-line
 * cap. Pure display shaping over the rules in attention.logic.ts; no Angular.
 */
import {
  Alert,
  AccountSignal,
  FocusKey,
  HOST_CRIT_PCT,
  HOST_WARN_PCT,
  HostSignal,
  Severity,
  WindowProjection,
  isSilent,
  projectWindow,
  Tile,
  seatCountText,
} from './attention.logic';
import { rateBasisText } from './pace.logic';
import { projectedText } from './projected-usage.logic';
import { formatDuration } from './wallboard.logic';

/** The category tag on a Needs attention row (web and mobile, WI 34539). */
export const ALERT_TAG_TEXT: Record<Alert['category'], string> = {
  waiting: 'WAITING',
  down: 'SEAT',
  owed: 'DUTY',
  context: 'CONTEXT',
  account: 'ACCOUNT',
  machine: 'MACHINE',
};

/** The Needs attention sub-line while a tile filter is on. */
export const FOCUS_TEXT: Record<Exclude<FocusKey, 'all'>, string> = {
  you: 'Your asks are in Needs you',
  waiting: 'Users waiting only · tap the tile again to clear',
  down: 'Seats down only · tap the tile again to clear',
  owed: 'Owed duties only · tap the tile again to clear',
  context: 'Context only · tap the tile again to clear',
  account: 'Accounts only · tap the tile again to clear',
  machine: 'Machines only · tap the tile again to clear',
};

export type TileTone = 'gold' | 'clear' | 'warn' | 'crit';

/** Needs you is gold; an empty tile is clear; context and owed duties are
 * warnings; the rest are critical when non-zero. */
export function tileTone(tile: Pick<Tile, 'key' | 'count'>): TileTone {
  if (tile.key === 'you') return 'gold';
  if (tile.count === 0) return 'clear';
  return tile.key === 'context' || tile.key === 'owed' ? 'warn' : 'crit';
}

/** CPU is shown, not alerted on (the ticket alerts on memory and disk). */
export const CPU_WARN_PCT = 80;
export const CPU_CRIT_PCT = 92;

/** 'calm' is a window that is live and present but on a normal pace (no
 * exception), distinct from a `null` cell (the window is absent or expired).
 * The two must never collapse to the same rendering (WI 34532 review): a
 * calm window says so, "No live window" is reserved for a genuinely missing
 * one. */
export type Level = Severity | 'ok' | 'calm';

export interface WindowCellVm {
  usedPct: number;
  /** Width of the faint "pace to reset" bar, capped at 100. */
  pacePct: number;
  text: string;
  level: Level;
  /** Hover text: the projection and its rate basis (Bug 34688 AC 4). */
  tip: string;
}

export interface AccountRowVm {
  key: string;
  name: string;
  seatsText: string;
  five: WindowCellVm | null;
  seven: WindowCellVm | null;
  worst: number;
}

function clamp(v: number): number {
  return Math.max(0, Math.min(100, v));
}

/** A real exception, as opposed to a calm-but-live cell or an absent one. */
function isException(c: WindowCellVm | null): boolean {
  return c !== null && c.level !== 'calm';
}

/** "Sat 4 PM" for resets days out, "in 1h 12m" inside a day. */
export function whenText(atMs: number, nowMs: number): string {
  const ms = atMs - nowMs;
  if (ms < 24 * 60 * 60_000) return `in ${formatDuration(ms)}`;
  return new Date(atMs).toLocaleString('en-US', {
    weekday: 'short',
    hour: 'numeric',
  });
}

export function windowCell(p: WindowProjection, nowMs: number): WindowCellVm {
  const used = Math.round(p.percent);
  const reset = whenText(p.resetAtMs, nowMs);
  const level: Level = p.level ?? 'calm';
  let text: string;
  if (p.percent >= 100) text = `${used}% · at the limit, resets ${reset}`;
  else if (p.hitsAtMs !== null)
    text = `${used}% · hits the limit ${whenText(p.hitsAtMs, nowMs)}, resets ${reset}`;
  // Every other live cell shows its projection, calm or not (Bug 34656).
  else
    text = `${used}% · projected ${projectedText(p)} at reset, resets ${reset}`;
  return {
    usedPct: clamp(p.percent),
    pacePct: clamp(p.projectedPct ?? p.percent),
    text,
    level,
    tip: `${projectionTip(p, nowMs)} · ${rateBasisText(p)}`,
  };
}

function projectionTip(p: WindowProjection, nowMs: number): string {
  if (p.percent >= 100) return 'at the limit';
  if (p.hitsAtMs !== null)
    return `hits the limit in about ${formatDuration(p.hitsAtMs - nowMs)}`;
  return p.projectedPct === null
    ? 'no projection yet'
    : `projected ${Math.round(p.projectedPct)}% at reset`;
}

const LEVEL_RANK: Record<Level, number> = {
  crit: 2,
  warn: 1,
  ok: 0,
  calm: 0,
};

/**
 * Accounts with a real exception window only (WI 34532 AC): a 5h or 7d window
 * projected to hit its limit before reset, or already at it. A normal pace
 * (e.g. 4-20% with no limit risk) renders no row here at all, it already
 * surfaces under Needs attention the moment `projectWindow` trips a level
 * (see `accountAlerts` in attention.logic.ts), so this card would otherwise
 * only ever duplicate that list. Worst projection first.
 *
 * The row-level filter is exception-only, but a row that qualifies (because
 * ITS OTHER window is in exception) still renders a real cell, calm or not,
 * for a window that is live and present (WI 34532 review): a `null` cell is
 * reserved for a window that is genuinely absent or expired, never for one
 * that is merely calm.
 */
export function buildAccountRows(
  accounts: AccountSignal[],
  nowMs: number
): AccountRowVm[] {
  return accounts
    .map((a) => {
      const cells = new Map(
        a.windows
          .filter((w) => w.resetAtMs > nowMs)
          .map((w) => projectWindow(w, nowMs))
          .map((p) => [p.window, windowCell(p, nowMs)] as const)
      );
      const five = cells.get('5h') ?? null;
      const seven = cells.get('7d') ?? null;
      const worst = Math.max(
        ...[five, seven].map((c) =>
          c ? LEVEL_RANK[c.level] * 1000 + c.pacePct : -1
        )
      );
      return {
        key: a.accountUpn.toLowerCase(),
        name: a.accountUpn.split('@')[0],
        seatsText: `${a.seatIds.length} seat${a.seatIds.length === 1 ? '' : 's'}`,
        five,
        seven,
        worst,
      };
    })
    .filter((r) => isException(r.five) || isException(r.seven))
    .sort((x, y) => y.worst - x.worst || x.name.localeCompare(y.name));
}

export interface MeterVm {
  label: string;
  value: number | null;
  text: string;
  level: Level;
}

export interface MachineRowVm {
  host: string;
  seatsText: string;
  silent: boolean;
  statusText: string;
  meters: MeterVm[];
  rank: number;
}

function meter(
  label: string,
  v: number | null,
  warn: number,
  crit: number,
  silent: boolean
): MeterVm {
  if (silent || v === null)
    return { label, value: null, text: 'n/a', level: 'ok' };
  return {
    label,
    value: clamp(v),
    text: `${Math.round(v)}%`,
    level: v >= crit ? 'crit' : v >= warn ? 'warn' : 'ok',
  };
}

/** "20s", "14m", "2h 05m": seconds under a minute so a live host reads live. */
export function ageText(ms: number): string {
  const safe = Math.max(0, ms);
  return safe < 60_000 ? `${Math.round(safe / 1000)}s` : formatDuration(safe);
}

/** "Seats: last known 16m ago (5)" when placement has aged past freshness but
 * is all we have; the plain count otherwise. Never reads as zero seats just
 * because the render clock walked past the freshness window (WI 34272 review,
 * P1). */
function seatsLabel(h: HostSignal, nowMs: number): string {
  if (h.seatIds.length === 0) return seatCountText(0);
  if (!h.seatsStale) return seatCountText(h.seatIds.length);
  const since =
    h.seatsLastObservedAtMs === null
      ? null
      : ageText(nowMs - h.seatsLastObservedAtMs);
  return since
    ? `Seats: last known ${since} ago (${h.seatIds.length})`
    : seatCountText(h.seatIds.length);
}

/** Machines, most seats first, ties by host name (WI 34687, Tim). A silent
 * host keeps its red row and SILENT pill but no longer jumps the order. */
export function buildMachineRows(
  hosts: HostSignal[],
  nowMs: number
): MachineRowVm[] {
  return [...hosts]
    .sort(
      (a, b) =>
        b.seatIds.length - a.seatIds.length || a.host.localeCompare(b.host)
    )
    .map((h): MachineRowVm => {
      const silent = isSilent(h, nowMs);
      const age =
        h.lastReportAtMs === null ? 'never' : ageText(nowMs - h.lastReportAtMs);
      const meters = [
        meter('CPU', h.cpuPct, CPU_WARN_PCT, CPU_CRIT_PCT, silent),
        meter('Mem', h.memPct, HOST_WARN_PCT, HOST_CRIT_PCT, silent),
        meter('Disk', h.diskPct, HOST_WARN_PCT, HOST_CRIT_PCT, silent),
      ];
      const hottest = Math.max(
        ...meters
          .slice(1)
          .map((m) => LEVEL_RANK[m.level] * 1000 + (m.value ?? 0))
      );
      return {
        host: h.host,
        seatsText: seatsLabel(h, nowMs),
        silent,
        statusText: silent
          ? `SILENT ${age.toUpperCase()}`
          : h.lastReportAtMs === null
            ? 'no report'
            : `${age} ago`,
        meters,
        rank: (silent ? 10_000 : 0) + hottest,
      };
    });
}

export interface Capped<T> {
  rows: T[];
  more: number;
}

/**
 * Caps a list to `max` rows with an honest overflow count (WI 34272 review,
 * P2): `rows.length + more` always equals `items.length`, so a fixed-capacity
 * card never silently truncates without saying so, and never reports a
 * nonzero "+N more" while still showing every row.
 */
export function capList<T>(items: T[], max: number): Capped<T> {
  return { rows: items.slice(0, max), more: Math.max(0, items.length - max) };
}

/**
 * How many fixed-height rows fit inside a container `containerPx` tall (WI
 * 34293 review, P2): a hardcoded MAX_* could not fit every dashboard card at
 * 1920x1080 (measured: two cards sharing a column can each need more row
 * space than the column has, even at the ideal flex split), so capacity is
 * measured from the real container height via ResizeObserver instead of
 * guessed. Floors to `floor` (never zero) and never returns a negative count
 * for a not-yet-measured (0px) container.
 */
export function rowsThatFit(
  containerPx: number,
  rowPx: number,
  floor = 1
): number {
  if (rowPx <= 0 || containerPx <= 0) return floor;
  return Math.max(floor, Math.floor(containerPx / rowPx));
}

/** Initials for the header avatar ("Tim Baldwin" -> "TB"). */
export function initials(name: string): string {
  const parts = name.split(/[\s@.]+/).filter(Boolean);
  return parts
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}
